import {
	type BlockHash,
	bigintize,
	getContractWithABI,
	getTokenAddress,
	isBlockHash,
	isTokenDefinition,
	type TokenIdentifier,
	type ValidChainId,
} from "@bracket/chainography";
import {
	getViemClients,
} from "@bracket/contractor-next";
import type { Config, } from "@wagmi/core";
import type { defineChain, } from "viem";



export declare type ClaimTokensProps = {
	token: TokenIdentifier;
	chainId?: ValidChainId;
	chain?: ReturnType<typeof defineChain>;
	config?: Config;
	amount: number|bigint;
	proof: Array<string>;
	attempt?: number;
}


/**
 * Make a BRKT claim!
 * @param {TokenIdentifer} token either BRKTETH, brktETH, 0x123jkasaddresss or an instance of TokenDefinition @see {TokenDefinition}
 * @param {number|bigint} amount either the amount of the token being claimed counted in the token (eg 1 for 1 brkt) or in its bigint eg(100000000000n for 1 brkt) 
 * @param {string[]} proof - the merkle tree proof for this claim as an array of proof portions
 * @param {chain} chain - OPTIONAL chain definition, default is the current chain definition
 * @param {ValidChainId} chainId in its numeric form, eg 1 or 42161, default is 1
 * @param {Config} config the wagmi/browser wallet config to execute against.
 * @returns {BlockHash} the resulting block hash.
 */
export const claimTokens = async ({
	token,
	amount,
	proof,
	config,
	chainId = 1,
}: ClaimTokensProps, ): Promise<BlockHash> => {

	try {

		if(typeof amount === "string") {
			throw new Error("invalid amount argued - argue either a number or bigint")
		}

		const [pclient, wclient] = await getViemClients({ config, chainId, });
		const { account, } = wclient;
		const [address, abi] = getContractWithABI("BracketEscrow", chainId);
		const token_address = isTokenDefinition(token) ? token.address : getTokenAddress(token);
		const bamount = typeof amount === "bigint" ? amount : bigintize(amount, token_address);

		try {

			const args = {
				address,
				abi,
				functionName: "claimTokens",
				args: [
					token_address,
					bamount,
					proof,
				],
				account,
			}

			const gas = await pclient.estimateGas(args)
			console.log("gas estimate for claimTokens", gas);

			const {
				request,
				result, // be great if we got boolean success flag.
			} = await pclient.simulateContract(args);

			const hash = await wclient.writeContract(request);
			await pclient.waitForTransactionReceipt({
				hash,
				timeout: 60_000,
			});
			if(isBlockHash(hash)) {
				return hash;
			}
			throw new Error("failed to obtain a resulting block hash");
		}
		catch( err ) {
			console.log(">>>>>>CLAIM ERROR>>>>", err);
			throw new Error(`transaction failed - ${err.message}`)
		}
	}
	catch( err ) {
		throw new Error(`@bracket/contractor-next::claimTokens - ${err.message}`);
	}

}
